import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  // We use a ref to track the "live" current workspace so fetchWorkspaces can
  // re-sync it without depending on currentWorkspace in its useCallback deps —
  // that dependency caused an infinite re-render loop: setCurrentWorkspace
  // creates a new object reference → fetchWorkspaces gets a new identity →
  // the useEffect that calls it re-runs → infinite flashing (which is what
  // the user filmed on the dashboard).
  const currentWorkspaceRef = useRef(null);
  useEffect(() => { currentWorkspaceRef.current = currentWorkspace; }, [currentWorkspace]);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/workspaces`);
      
      if (response.ok) {
        const data = await response.json();
        setWorkspaces(data);

        // NOTE: We deliberately do NOT auto-redirect to /portal here based
        // on memberships. That logic lives in LoginDark.jsx postAuthDest()
        // so it fires once at login-time, not on every page navigation.
        // (Moving it out fixes a bug where owners who tested their own
        // client portal got bounced to /portal forever after login.)

        const active = currentWorkspaceRef.current;
        if (data.length > 0 && !active) {
          // Prefer an explicit `?ws=<id>` URL param (used by admin-view deep
          // links) over the last-active-workspace stored in localStorage,
          // otherwise fall back to that, otherwise pick the first.
          let paramWsId = null;
          try {
            paramWsId = new URLSearchParams(window.location.search).get('ws');
          } catch (e) { /* older browsers */ }
          const paramWs = paramWsId ? data.find(w => w.workspace_id === paramWsId) : null;
          let savedWsId = null;
          try { savedWsId = localStorage.getItem('currentWorkspaceId'); } catch (e) { /* iframe */ }
          const savedWs = savedWsId ? data.find(w => w.workspace_id === savedWsId) : null;
          setCurrentWorkspace(paramWs || savedWs || data[0]);
        } else if (active) {
          // Re-sync the active workspace object so updates (icon, name, etc.)
          // ripple to every consumer immediately after a refresh — BUT only
          // when something actually changed, to avoid unnecessary re-renders.
          const matched = data.find(w => w.workspace_id === active.workspace_id);
          if (matched && (
            matched.icon_emoji !== active.icon_emoji ||
            matched.icon_logo_url !== active.icon_logo_url ||
            matched.name !== active.name ||
            matched.role !== active.role
          )) {
            setCurrentWorkspace(matched);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    } finally {
      setLoading(false);
    }
  }, []); // ← no deps — uses ref instead. Breaks the flashing loop.

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const selectWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
    try { localStorage.setItem('currentWorkspaceId', workspace.workspace_id); } catch (e) { /* iframe */ }
  };

  const createWorkspace = async (name, currency = 'USD', workspaceType = 'business', industryTemplate = 'general', templateKey = null) => {
    try {
      const response = await apiFetch(`${API_URL}/api/workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, currency, workspace_type: workspaceType, industry_template: industryTemplate, template_key: templateKey }),
      });
      
      if (response.ok) {
        const newWorkspace = await response.json();
        setWorkspaces(prev => [...prev, newWorkspace]);
        setCurrentWorkspace(newWorkspace);
        try { localStorage.setItem('currentWorkspaceId', newWorkspace.workspace_id); } catch (e) { /* iframe */ }
        toast.success('Workspace created successfully');
        return newWorkspace;
      } else {
        const error = await response.json();
        if (response.status === 402) {
          const detail = error?.detail || {};
          toast.error(detail.message || 'A subscription plan is required to create a workspace.');
          window.location.href = detail.upgrade_path || '/welcome?cancelled=true&plan=starter&cadence=monthly';
          return null;
        }
        toast.error(typeof error.detail === 'string' ? error.detail : 'Failed to create workspace');
        return null;
      }
    } catch (error) {
      console.error('Failed to create workspace:', error);
      toast.error('Failed to create workspace');
      return null;
    }
  };

  const userRole = currentWorkspace?.role || null;

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      currentWorkspace,
      loading,
      selectWorkspace,
      createWorkspace,
      refreshWorkspaces: fetchWorkspaces,
      userRole,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
